import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, inject, input, output } from '@angular/core';
import '@maillune/sdk';
import { MailluneService } from '../../../../../services/maillune.service';

export interface IMailluneMergeTag {
    name: string;
    value: string;
    sample?: string;
}

export interface IMailluneTemplateEditorConfig {
    // Short-lived session token from the v2 maillune/token route.
    token: string;
    // Mirrors the Beefree uid convention: ORG-<organizationId>
    userId: string;
    // Maillune BlockTree JSON — its own native format. Preferred when present.
    templateJson?: any;
    // Existing Beefree template JSON, handed to the SDK so it can convert/import it.
    beefreeTemplate?: any;
    locale?: string;
    theme?: 'light' | 'dark' | 'auto';
    accentColor?: string;
    mergeTags?: IMailluneMergeTag[];
}

// The published SDK ships no .d.ts, so the element surface we use is typed here.
export interface MailluneEmailBuilderElement extends HTMLElement {
    load(blockTree: any): void;
    getJson(): any;
    exportHtml(): Promise<string | null>;
    updateConfig(config: any): void;
    isDirty(): boolean;
    openTemplates(): void;
    // Optional across SDK versions.
    captureSnapshot?(): Promise<Blob | null>;
    getThumbnail?(): Promise<Blob | null>;
}

export interface IMailluneTemplateSaved {
    json: any;
    html: string;
    thumbnail?: string;
}

@Component({
    selector: 'maillune-template-editor',
    templateUrl: './maillune-template-editor.component.html',
    styleUrls: ['./maillune-template-editor.component.scss'],
    imports: []
})
export class MailluneTemplateEditorComponent implements AfterViewInit, OnDestroy {
    private maillune = inject(MailluneService);

    config = input.required<IMailluneTemplateEditorConfig>();
    templateSaved = output<IMailluneTemplateSaved>();

    @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;

    private builder: MailluneEmailBuilderElement | null = null;
    private destroyed = false;

    ngAfterViewInit(): void {
        const config = this.config();
        const builder = document.createElement('email-builder') as MailluneEmailBuilderElement;
        builder.setAttribute('api-key', config.token);
        builder.setAttribute('user-id', config.userId);
        // Full replacement: no built-in toolbar — the surrounding drawer supplies Save/Cancel,
        // and placeholders are provided as merge-tag pills instead of a Beefree placeholders drawer.
        builder.setAttribute('headless', '');
        builder.setAttribute('theme', config.theme || 'auto');

        // Runtime config must be set before the element is attached — the SDK mounts on
        // connectedCallback and reads locale/mergeTags/beefreeTemplate at mount time.
        // theme/accentColor are withheld here: updateConfig() applies them synchronously via
        // applyTheme(), which reads this.shadow — only created once connectedCallback runs on
        // attach. Passing them pre-attach throws "this.shadow is undefined".
        builder.updateConfig({
            locale: config.locale || 'en',
            mergeTags: config.mergeTags || [],
            // Hand the Beefree design to Maillune for conversion/import on mount.
            beefreeTemplate: config.beefreeTemplate
        });

        // <email-builder> creates its shadow root in connectedCallback and cannot survive being
        // moved afterwards ("Shadow root cannot be created on a host which already hosts a shadow
        // tree"). Inside a p-drawer with appendTo="body", PrimeNG moves the container into
        // document.body after this view is created, so attaching now would connect it twice.
        // Attach only once the container has stopped moving.
        this.builder = builder;
        this.attachWhenSettled(builder, config);
    }

    /** Attaches the builder once the container is in the document and its ancestry is stable for two frames. */
    private attachWhenSettled(builder: MailluneEmailBuilderElement, config: IMailluneTemplateEditorConfig): void {
        const host = this.container.nativeElement;
        const rootOf = (el: HTMLElement): Element | null => {
            let node: HTMLElement = el;
            while (node.parentElement && node.parentElement !== document.body) {
                node = node.parentElement;
            }
            return node.parentElement === document.body ? node : null;
        };

        let previousRoot: Element | null = null;
        let stableFrames = 0;
        const check = (attempt: number) => {
            if (this.destroyed) {
                return;
            }
            const root = host.isConnected ? rootOf(host) : null;
            stableFrames = root && root === previousRoot ? stableFrames + 1 : 0;
            previousRoot = root;

            if (stableFrames >= 2 || (root && attempt >= 120)) {
                host.appendChild(builder);
                builder.updateConfig({ accentColor: config.accentColor });
                // Prefer a native Maillune design. A Beefree design goes through load() too: the SDK
                // converts it there (normalizeToBlockTree), whereas the beefreeTemplate config
                // option is ignored by the installed SDK and left the canvas empty.
                const design = config.templateJson ?? config.beefreeTemplate;
                if (design) {
                    builder.load(design);
                }
                return;
            }
            if (attempt >= 300) {
                console.warn('Maillune editor: container never settled in the document; editor not mounted.');
                return;
            }
            requestAnimationFrame(() => check(attempt + 1));
        };
        requestAnimationFrame(() => check(0));
    }

    // Mirrors BeefreeTemplateEditorComponent.save(): emits the design JSON, rendered HTML,
    // and a thumbnail so previews keep working without any Beefree linkage.
    async save(): Promise<void> {
        if (!this.builder) return;
        const json = this.builder.getJson();
        const html = await this.builder.exportHtml();
        if (html == null) {
            throw new Error('Maillune HTML export failed');
        }
        const thumbnail = await this.captureThumbnail();
        this.templateSaved.emit({ json, html, thumbnail });
    }

    // Opens the SDK's own built-in template browser (rendered in its shadow DOM).
    // Picking a template loads it into the canvas; no event needs to come back to us.
    openTemplates(): void {
        this.builder?.openTemplates();
    }

    // Persists the current design into Maillune's reusable template library (distinct
    // from save(), which emits json/html/thumbnail for the host's own "Save").
    async saveAsTemplate(name: string): Promise<void> {
        if (!this.builder) {
            throw new Error('Maillune editor not ready');
        }
        const blockTree = this.builder.getJson();
        const config = this.config();
        await this.maillune.saveAsTemplate(config.token, config.userId, name, blockTree);
    }

    private async captureThumbnail(): Promise<string | undefined> {
        const capture = this.builder?.captureSnapshot ?? this.builder?.getThumbnail;
        if (!capture) return undefined;
        try {
            const blob = await capture.call(this.builder);
            if (!blob) return undefined;
            return await this.blobToDataUrl(blob);
        } catch (e) {
            console.warn('Maillune thumbnail capture failed:', e);
            return undefined;
        }
    }

    private blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.builder?.remove();
        this.builder = null;
    }
}
